/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * Root reducer module that combines all slice reducers
 * Handles global state transformations and persistence lifecycle events
 * @module redux/root-reducer
 */

import { combineReducers } from "redux";
import { PURGE, REHYDRATE } from "redux-persist";

import { AUTH_USER_UNAUTHORIZED } from "./actions/auth-actions";
import accountReducer from "./slices/accountSlice";
import assetReducer from "./slices/assetSlice";
import authReducer, { getInitialState as getAuthInitialState } from "./slices/authSlice";
import avLiveReducer from "./slices/avLiveSlice";
import balanceReducer from "./slices/balanceSlice";
import betslipReducer, { getInitialState as getBetslipInitialState } from "./slices/betslipSlice";
import betTypeReducer, { getBetTypeInitialState } from "./slices/betTypeSlice";
import bonusReducer from "./slices/bonusSlice";
import cashoutReducer from "./slices/cashoutSlice";
import chatReducer, { getInitialState as getChatInitialState } from "./slices/chatSlice";
import cmsReducer from "./slices/cmsSlice";
import countryReducer from "./slices/countrySlice";
import couponReducer from "./slices/couponSlice";
import currencyReducer from "./slices/currencySlice";
import favouriteReducer from "./slices/favouriteSlice";
import jackpotReducer from "./slices/jackpotSlice";
import languagesReducer from "./slices/languageSlice";
import liveCalendarReducer from "./slices/liveCalendarSlice";
import liveReducer, { getInitialState as getLiveInitialState } from "./slices/liveSlice";
import matchStatusesReducer from "./slices/matchStatusSlice";
import periodsReducer from "./slices/periodSlice";
import recommenderReducer from "./slices/recommenderSlice";
import referralReducer from "./slices/referralSlice";
import resultReducer from "./slices/resultSlice";
import retailAccountReducer, { getRetailInitialState } from "./slices/retailAccountSlice";
import retailTillReducer from "./slices/retailTillSlice";
import retailTransactionReducer from "./slices/retailTransactionSlice";
import securityQuestionReducer from "./slices/securityQuestionSlice";
import solidGamingReducer from "./slices/solidGamingSlice";
import sportDataReducer from "./slices/sportDataSlice";
import sportReducer from "./slices/sportSlice";
import sportsTreeReducer from "./slices/sportsTreeSlice";
import totoReducer from "./slices/totoSlice";
import vaixReducer from "./slices/vaixSlice";
import backdoor from "./utils/reduxBackdoor";

/**
 * Account reducer wrapper
 * Handles special global actions for account state:
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current account state
 * @param {Object} action - Redux action
 * @returns {Object} New account state
 */
function accountReducerWrapper(state, action) {
  switch (action.type) {
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return accountReducer(undefined, action);
    default:
      return accountReducer(state, action);
  }
}

/**
 * Auth reducer wrapper
 * Handles special global actions and persistence for authentication state:
 * - Selectively preserves auth state during rehydration
 * - Updates backdoor utilities with language and price format for other components
 * - Resets credentials on unauthorized actions while preserving preferences
 * - Completely resets on PURGE action
 *
 * @param {Object} state - Current auth state
 * @param {Object} action - Redux action
 * @returns {Object} New auth state
 */
function authReducerWrapper(state, action) {
  switch (action.type) {
    case REHYDRATE: {
      // Hack: `liveSlice` uses `language`, `priceFormat` but has no direct access to this state.
      // Therefore, update them when in redux values changed.
      backdoor.language = action.payload?.auth?.language;
      backdoor.priceFormat = action.payload?.auth?.priceFormat;

      return authReducer(
        getAuthInitialState({
          accountId: action.payload?.auth?.accountId,
          authLoginURL: action.payload?.auth?.authLoginURL,
          authToken: action.payload?.auth?.authToken,
          currencyCode: action.payload?.auth?.currencyCode,
          desktopTheme: action.payload?.auth?.desktopTheme,
          desktopView: action.payload?.auth?.desktopView,
          isSplitModePreferred: action.payload?.auth?.isSplitModePreferred ?? true,
          language: action.payload?.auth?.language,
          loggedIn: action.payload?.auth?.loggedIn,
          mobileTheme: action.payload?.auth?.mobileTheme,
          mobileView: action.payload?.auth?.mobileView,
          playerRequestedDesktopView: action.payload?.auth?.playerRequestedDesktopView,
          playerRequestedMobileView: action.payload?.auth?.playerRequestedMobileView,
          playerRequestedPriceFormat: action.payload?.auth?.playerRequestedPriceFormat,
          priceFormat: action.payload?.auth?.priceFormat,
          rememberedUsername: action.payload?.auth?.rememberedUsername,
          timezoneOffset: action.payload?.auth?.timezoneOffset,
          username: action.payload?.auth?.username,
        }),
        action,
      );
    }
    case AUTH_USER_UNAUTHORIZED:
      return authReducer(
        getAuthInitialState({
          ...state,
          accountId: null,
          authToken: null,
          loggedIn: false,
          username: null,
        }),
        action,
      );
    case PURGE:
      return authReducer(undefined, action);
    default:
      return authReducer(state, action);
  }
}

/**
 * Asset reducer wrapper
 * Handles special global actions for asset state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current asset state
 * @param {Object} action - Redux action
 * @returns {Object} New asset state
 */
function assetReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return assetReducer(undefined, action);
    default:
      return assetReducer(state, action);
  }
}

/**
 * Audio/Video live stream reducer wrapper
 * Handles special global actions for AV live streaming state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current AV live state
 * @param {Object} action - Redux action
 * @returns {Object} New AV live state
 */
function avLiveReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return avLiveReducer(undefined, action);
    default:
      return avLiveReducer(state, action);
  }
}

/**
 * Balance reducer wrapper
 * Handles special global actions for user balance state
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current balance state
 * @param {Object} action - Redux action
 * @returns {Object} New balance state
 */
function balanceReducerWrapper(state, action) {
  switch (action.type) {
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return balanceReducer(undefined, action);
    default:
      return balanceReducer(state, action);
  }
}

/**
 * Betslip reducer wrapper
 * Handles special global actions and persistence for betslip state
 * - Restores persisted betslip data during rehydration
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current betslip state
 * @param {Object} action - Redux action
 * @returns {Object} New betslip state
 */
function betslipReducerWrapper(state, action) {
  switch (action.type) {
    case REHYDRATE: {
      return betslipReducer(
        getBetslipInitialState(
          action.payload?.betslip?.betslipData,
          action.payload?.betslip?.jackpotBetslipData,
          action.payload?.betslip?.brVirtualBetslipData,
          action.payload?.betslip?.krVirtualBetslipData,
        ),
        action,
      );
    }
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return betslipReducer(undefined, action);
    default:
      return betslipReducer(state, action);
  }
}

/**
 * Bet type reducer wrapper
 * Handles special global actions and persistence for bet type definitions
 * - Restores persisted bet type data during rehydration
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current bet type state
 * @param {Object} action - Redux action
 * @returns {Object} New bet type state
 */
function betTypeReducerWrapper(state, action) {
  switch (action.type) {
    case REHYDRATE: {
      return betTypeReducer(
        getBetTypeInitialState(action.payload?.betType?.betTypes, action.payload?.betType?.language),
        action,
      );
    }
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return betTypeReducer(undefined, action);
    default:
      return betTypeReducer(state, action);
  }
}

/**
 * Bonus reducer wrapper
 * Handles special global actions for user bonus state
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current bonus state
 * @param {Object} action - Redux action
 * @returns {Object} New bonus state
 */
function bonusReducerWrapper(state, action) {
  switch (action.type) {
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return bonusReducer(undefined, action);
    default:
      return bonusReducer(state, action);
  }
}

/**
 * Cashout reducer wrapper
 * Handles special global actions for cashout state
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current cashout state
 * @param {Object} action - Redux action
 * @returns {Object} New cashout state
 */
function cashoutReducerWrapper(state, action) {
  switch (action.type) {
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return cashoutReducer(undefined, action);
    default:
      return cashoutReducer(state, action);
  }
}

/**
 * Chat reducer wrapper
 * Handles special global actions and persistence for chat session state
 * - Restores persisted chat session data during rehydration
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current chat state
 * @param {Object} action - Redux action
 * @returns {Object} New chat state
 */
function chatReducerWrapper(state, action) {
  switch (action.type) {
    case REHYDRATE: {
      return chatReducer(getChatInitialState(action.payload?.chat?.sessionId, action.payload?.chat?.startTime), action);
    }
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return chatReducer(undefined, action);
    default:
      return chatReducer(state, action);
  }
}

/**
 * CMS reducer wrapper
 * Handles special global actions for content management system state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current CMS state
 * @param {Object} action - Redux action
 * @returns {Object} New CMS state
 */
function cmsReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return cmsReducer(undefined, action);
    default:
      return cmsReducer(state, action);
  }
}

/**
 * Country reducer wrapper
 * Handles special global actions for country data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current country state
 * @param {Object} action - Redux action
 * @returns {Object} New country state
 */
function countryReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return countryReducer(undefined, action);
    default:
      return countryReducer(state, action);
  }
}

/**
 * Coupon reducer wrapper
 * Handles special global actions for coupon data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current coupon state
 * @param {Object} action - Redux action
 * @returns {Object} New coupon state
 */
function couponReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return couponReducer(undefined, action);
    default:
      return couponReducer(state, action);
  }
}

/**
 * Currency reducer wrapper
 * Handles special global actions for currency data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current currency state
 * @param {Object} action - Redux action
 * @returns {Object} New currency state
 */
function currencyReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return currencyReducer(undefined, action);
    default:
      return currencyReducer(state, action);
  }
}

/**
 * Favourite reducer wrapper
 * Handles special global actions for user favorites state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current favourites state
 * @param {Object} action - Redux action
 * @returns {Object} New favourites state
 */
function favouriteReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return favouriteReducer(undefined, action);
    default:
      return favouriteReducer(state, action);
  }
}

/**
 * Jackpot reducer wrapper
 * Handles special global actions for jackpot data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current jackpot state
 * @param {Object} action - Redux action
 * @returns {Object} New jackpot state
 */
function jackpotReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return jackpotReducer(undefined, action);
    default:
      return jackpotReducer(state, action);
  }
}

/**
 * Languages reducer wrapper
 * Handles special global actions for available languages state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current languages state
 * @param {Object} action - Redux action
 * @returns {Object} New languages state
 */
function languagesReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return languagesReducer(undefined, action);
    default:
      return languagesReducer(state, action);
  }
}

/**
 * Live calendar reducer wrapper
 * Handles special global actions for live event calendar state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current live calendar state
 * @param {Object} action - Redux action
 * @returns {Object} New live calendar state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function liveCalendarReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return liveCalendarReducer(undefined, action);
    default:
      return liveCalendarReducer(state, action);
  }
}

/**
 * Live events reducer wrapper
 * Handles special global actions and persistence for live event state:
 * - Restores persisted favorites and multi-view selections during rehydration
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current live events state
 * @param {Object} action - Redux action
 * @returns {Object} New live events state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function liveReducerWrapper(state, action) {
  switch (action.type) {
    case REHYDRATE: {
      return liveReducer(
        getLiveInitialState(action.payload?.live?.liveFavourites, action.payload?.live?.multiViewEventIds),
        action,
      );
    }
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return liveReducer(undefined, action);
    default:
      return liveReducer(state, action);
  }
}

/**
 * Periods reducer wrapper
 * Handles special global actions for game periods state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current periods state
 * @param {Object} action - Redux action
 * @returns {Object} New periods state
 */
function periodsReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return periodsReducer(undefined, action);
    default:
      return periodsReducer(state, action);
  }
}

/**
 * Match statuses reducer wrapper
 * Handles special global actions for match statuses state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current match statuses state
 * @param {Object} action - Redux action
 * @returns {Object} New match statuses state
 */
function matchStatusesReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return matchStatusesReducer(undefined, action);
    default:
      return matchStatusesReducer(state, action);
  }
}

/**
 * Recommender reducer wrapper
 * Handles special global actions for recommendation engine state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current recommender state
 * @param {Object} action - Redux action
 * @returns {Object} New recommender state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function recommenderReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return recommenderReducer(undefined, action);
    default:
      return recommenderReducer(state, action);
  }
}

/**
 * Referral reducer wrapper
 * Handles special global actions for referral system state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current referral state
 * @param {Object} action - Redux action
 * @returns {Object} New referral state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function referralReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return referralReducer(undefined, action);
    default:
      return referralReducer(state, action);
  }
}

/**
 * Result reducer wrapper
 * Handles special global actions for event results state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current result state
 * @param {Object} action - Redux action
 * @returns {Object} New result state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function resultReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return resultReducer(undefined, action);
    default:
      return resultReducer(state, action);
  }
}

/**
 * Retail account reducer wrapper
 * Handles special global actions and persistence for retail account state:
 * - Restores selected player account during rehydration
 * - Resets state on user unauthorized event
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current retail account state
 * @param {Object} action - Redux action
 * @returns {Object} New retail account state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function retailAccountReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case REHYDRATE: {
      return retailAccountReducer(
        getRetailInitialState(action.payload?.retailAccount?.selectedPlayerAccountId),
        action,
      );
    }
    case AUTH_USER_UNAUTHORIZED:
    case PURGE:
      return retailAccountReducer(undefined, action);
    default:
      return retailAccountReducer(state, action);
  }
}

/**
 * Retail till reducer wrapper
 * Handles special global actions for retail till state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current retail till state
 * @param {Object} action - Redux action
 * @returns {Object} New retail till state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function retailTillReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return retailTillReducer(undefined, action);
    default:
      return retailTillReducer(state, action);
  }
}

/**
 * Retail transaction reducer wrapper
 * Handles special global actions for retail transaction state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current retail transaction state
 * @param {Object} action - Redux action
 * @returns {Object} New retail transaction state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function retailTransactionReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return retailTransactionReducer(undefined, action);
    default:
      return retailTransactionReducer(state, action);
  }
}

/**
 * Security question reducer wrapper
 * Handles special global actions for security questions state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current security question state
 * @param {Object} action - Redux action
 * @returns {Object} New security question state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function securityQuestionReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return securityQuestionReducer(undefined, action);
    default:
      return securityQuestionReducer(state, action);
  }
}

/**
 * Solid Gaming reducer wrapper
 * Handles special global actions for Solid Gaming state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current Solid Gaming state
 * @param {Object} action - Redux action
 * @returns {Object} New Solid Gaming state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function solidGamingReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return solidGamingReducer(undefined, action);
    default:
      return solidGamingReducer(state, action);
  }
}

/**
 * Sport reducer wrapper
 * Handles special global actions for sport data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current sport state
 * @param {Object} action - Redux action
 * @returns {Object} New sport state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function sportReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return sportReducer(undefined, action);
    default:
      return sportReducer(state, action);
  }
}

/**
 * Sport data reducer wrapper
 * Handles special global actions for detailed sport data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current sport data state
 * @param {Object} action - Redux action
 * @returns {Object} New sport data state
 */
function sportDataReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return sportDataReducer(undefined, action);
    default:
      return sportDataReducer(state, action);
  }
}

/**
 * Sports tree reducer wrapper
 * Handles special global actions for sports hierarchical data
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current sports tree state
 * @param {Object} action - Redux action
 * @returns {Object} New sports tree state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function sportsTreeReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return sportsTreeReducer(undefined, action);
    default:
      return sportsTreeReducer(state, action);
  }
}

/**
 * TOTO reducer wrapper
 * Handles special global actions for TOTO games state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current TOTO state
 * @param {Object} action - Redux action
 * @returns {Object} New TOTO state
 * @todo Add 401 action to clean the storage
 * @todo Dispatch `PURGE` action when error occurs
 */
function totoReducerWrapper(state, action) {
  switch (action.type) {
    // Todo: add 401 action to clean the storage.
    // Todo: dispatch `PURGE` action when error occurs.
    case PURGE:
      return totoReducer(undefined, action);
    default:
      return totoReducer(state, action);
  }
}

/**
 * VAIX data reducer wrapper
 * Handles special global actions for VAIX (AI/ML) data state
 * - Resets state on redux-persist PURGE action
 *
 * @param {Object} state - Current VAIX data state
 * @param {Object} action - Redux action
 * @returns {Object} New VAIX data state
 */
function vaixDataReducerWrapper(state, action) {
  switch (action.type) {
    case PURGE:
      return vaixReducer(undefined, action);
    default:
      return vaixReducer(state, action);
  }
}

const reducer = combineReducers({
  account: accountReducerWrapper,
  asset: assetReducerWrapper,
  auth: authReducerWrapper,
  avlive: avLiveReducerWrapper,
  balance: balanceReducerWrapper,
  betType: betTypeReducerWrapper,
  betslip: betslipReducerWrapper,
  bonus: bonusReducerWrapper,
  cashout: cashoutReducerWrapper,
  chat: chatReducerWrapper,
  cms: cmsReducerWrapper,
  country: countryReducerWrapper,
  coupon: couponReducerWrapper,
  currency: currencyReducerWrapper,
  favourite: favouriteReducerWrapper,
  jackpot: jackpotReducerWrapper,
  language: languagesReducerWrapper,
  live: liveReducerWrapper,
  liveCalendar: liveCalendarReducerWrapper,
  matchStatus: matchStatusesReducerWrapper,
  period: periodsReducerWrapper,
  recommender: recommenderReducerWrapper,
  referral: referralReducerWrapper,
  result: resultReducerWrapper,
  retailAccount: retailAccountReducerWrapper,
  retailTill: retailTillReducerWrapper,
  retailTransaction: retailTransactionReducerWrapper,
  securityQuestion: securityQuestionReducerWrapper,
  solidGaming: solidGamingReducerWrapper,
  sport: sportReducerWrapper,
  sportData: sportDataReducerWrapper,
  sportsTree: sportsTreeReducerWrapper,
  toto: totoReducerWrapper,
  vaix: vaixDataReducerWrapper,
});

export default reducer;
